import type {
	Address,
	BusinessSupplier,
	Contact,
	Customer,
	Estimate,
	Job,
	Property,
	PurchaseOrder,
	PurchaseOrderDeliveryStatus,
	ScheduledJob,
	ScheduledJobClock,
	Supplier,
	User,
} from '@prisma/client';
import { z } from 'zod';
import type { MobileWorkOrder } from './workOrder.schema';

export enum FieldJobType {
	INSTALL = 1,
	PURCHASE_ORDER,
	FABRICATION,
	MEASUREMENT,
	UNSCHEDULED,
	CUSTOM,
	SERVICE,
}

export enum FilterDisplayOptions {
	MY_JOBS = 'My Jobs',
	SCHEDULED = 'Scheduled',
	ALL_JOBS = 'All Jobs',
	PURCHASE_ORDERS = 'Purchase Orders',
	SHOP_WORK = 'Fabrication',
	UNSCHEDULED_JOBS = 'Open Jobs',
}

export type FieldJobDelivery = {
	id: number;
	deliveryNumber: string;
	status: PurchaseOrderDeliveryStatus;
	expectedArrivalDate: Date | null;
	actualArrivalDate: Date | null;
};

// Shape returned by the FieldService Prisma queries. The PO-level arrival
// columns were removed, so the raw row has no expected/actualArrivalDate; those
// are derived from deliveries in FieldService.queryToCard.
export type FieldJobPurchaseOrderRaw = Pick<PurchaseOrder, 'id' | 'poNumber' | 'externalPONumber' | 'anticipatedCost' | 'actualCost' | 'status'> & {
	purchaseOrderDeliveries: FieldJobDelivery[];
	businessSupplier: Partial<BusinessSupplier> & {
		supplier: Pick<Supplier, 'name'>;
	};
	isStockPO?: boolean;
};

export type FieldJobPurchaseOrder = FieldJobPurchaseOrderRaw & {
	// Derived in FieldService.queryToCard from the PO's deliveries (the PO-level
	// arrival columns were removed): expected = earliest pending delivery, actual
	// = latest received delivery. Kept on the DTO so existing (incl. legacy mobile)
	// consumers that read these off the field card continue to work.
	expectedArrivalDate: Date | null;
	actualArrivalDate: Date | null;
};

//For use within the FieldService class when querying initial jobs
export type FieldJobRaw = Pick<Job, 'id' | 'internalJobId' | 'deadline' | 'name' | 'jobPhase' | 'problemReason' | 'isTemplate'> & {
	address: Address | null;
	customer?: Customer & {
		contacts: Contact[];
	};
	property?: Property | null;
	jobOwner: User;
	purchaseOrders?: FieldJobPurchaseOrderRaw[];
	ScheduledJobs?: (ScheduledJob & {
		usersOnScheduledJob: {
			assignedUser: {
				id: number;
				email: string | null;
				phone: string;
				firstName: string;
				lastName: string;
			};
			additionalUsers: {
				id: number;
				email: string | null;
				phone: string;
				firstName: string;
				lastName: string;
			}[];
		}[];
		ScheduledJobClock: ScheduledJobClock[];
		workOrders: MobileWorkOrder[];
	})[];
	estimates: Estimate[];
};

export type FieldJobCard = {
	jobCardId: string; //Use for react key, frontend ID generation

	jobId: number;

	job: Pick<Job, 'name' | 'deadline' | 'id' | 'internalJobId' | 'jobPhase' | 'problemReason'> & {
		jobCategory?: { name: string } | null;
		jobOwner: Pick<User, 'id' | 'firstName' | 'lastName' | 'phone'>;
		customer?: Customer & {
			contacts: Contact[];
		};
	};

	property?: Property | null;
	purchaseOrder?: FieldJobPurchaseOrder;
	supplierName?: string;

	customer?: Customer & {
		contacts: Contact[];
	};
	address?: Address;
	estimates: Estimate[];

	fieldJobType: FieldJobType;
	customJobType?: string | null;

	scheduledJob?: ScheduledJob & {
		usersOnScheduledJob: {
			assignedUser: {
				id: number;
				email: string | null;
				phone: string;
				firstName: string;
				lastName: string;
			};
			additionalUsers: {
				id: number;
				email: string | null;
				phone: string;
				firstName: string;
				lastName: string;
			}[];
		}[];
		ScheduledJobClock: ScheduledJobClock[];
		workOrders: MobileWorkOrder[];
	};

	isOverdue?: boolean;
	isComplete?: boolean;

	start?: Date | null;
	startTime?: string;

	end?: Date | null;
	endTime?: string;

	startedAt?: Date | null;
	completedAt?: Date | null;
};

export const AllTasksQueryInput = z.object({
	date: z.date(),
	filters: z.object({
		search: z.string().trim().optional(),
		checkedDisplayOpts: z.array(z.string()),
		checkedJobTypes: z.array(z.number()),
		checkedCustomers: z.array(z.number()),
	}),
	limit: z.number(),
	cursor: z.number().optional(),
});

export type AllTasksQueryInputType = z.infer<typeof AllTasksQueryInput>;
